<script setup>
import GbButton from '@/components/GbButton.vue'
import Dialog from 'primevue/dialog'
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'

const props = defineProps({
  uuid: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  downloadLinks: {
    type: Array,
    required: true
  },
  screenshots: {
    type: Array,
    required: true
  },
  numPages: {
    type: Number,
    default: () => {
      return 0
    }
  },
  googleDriveLinks: {
    required: false,
    type: Array,
    default: () => {
      return []
    }
  },
  language: {
    type: String,
    default: ''
  }
})

const visible = ref(true)
const keyForScreenshotIndex = (index) => {
  return `${props.uuid}-${index}`
}
const { isAuthenticated, isTrial, isLicenseExpired } = storeToRefs(useUserStore())
const imageRefs = ref([])

const previewImageTransformations = computed(() => {
  // https://docs.imagekit.io/features/image-transformations/overlay-using-layers#add-text-over-image
  const watermarkTransformations =
    'l-text,i-GOALBOOK PREVIEW,ff-Lato,co-00769F,bg-ddddddd,w-400,al-7,pa-30_20,r-5,fs-40,l-end'
  return !isAuthenticated.value || isTrial.value || isLicenseExpired.value
    ? { tr: `w-500,${watermarkTransformations}` }
    : { tr: 'w-500' }
})

const advancePage = (steps) => {
  // Grab the screenshot element bounding boxes
  let screenshotBoundingBoxes = []
  for (let i = 0; i < imageRefs.value.length; i++) {
    screenshotBoundingBoxes.push(imageRefs.value[i].getBoundingClientRect())
  }

  // Figure out which screenshot index should be the previous and next screenshots
  let prevIndex,
    nextIndex = 0
  const topPadding = 80
  for (let index = 0; index < screenshotBoundingBoxes.length; index++) {
    // Currently viewing the last screenshot
    if (index == screenshotBoundingBoxes.length - 1) {
      if (screenshotBoundingBoxes[index].top < topPadding) {
        prevIndex = index
      } else {
        prevIndex = index - 1
      }
      nextIndex = index
      break
    }
    // Currently screenshot is exactly aligned with top
    else if (Math.floor(screenshotBoundingBoxes[index].top) === topPadding) {
      prevIndex = index - 1
      nextIndex = index + 1
      break
    }
    // Somewhere in between two screen shots
    else if (
      screenshotBoundingBoxes[index].top < topPadding &&
      Math.floor(screenshotBoundingBoxes[index + 1].top) > topPadding
    ) {
      prevIndex = index
      nextIndex = index + 1
      break
    }
  }

  // From current previous go to next screen shot indices
  // based on step direction
  if (steps < 0) {
    const indexToView = Math.max(prevIndex, 0)
    imageRefs.value[indexToView].scrollIntoView({
      behavior: 'smooth'
    })
  } else if (steps > 0) {
    const indexToView = Math.min(nextIndex, screenshotBoundingBoxes.length - 1)
    imageRefs.value[indexToView].scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<template>
  <Dialog
    :aria-labelledby="uuid + '-resource-modal-title'"
    v-model:visible="visible"
    :dismissableMask="true"
    modal
    :pt="{
      root: 'border-none h-full w-full',
      mask: {
        style: 'backdrop-filter: blur(2px)'
      }
    }"
    :blockScroll="true"
  >
    <template #container="{ closeCallback }">
      <div class="fixed left-0 top-0 h-full w-full overflow-auto">
        <div class="fixed left-0 top-0 z-20 w-full bg-black p-4">
          <div class="container flex justify-between align-middle">
            <span class="self-center text-white" :id="uuid + '-resource-modal-title'">{{
              title
            }}</span>
            <ResourceDownloadButton
              :resource="{
                uuid,
                type,
                title,
                download_links: downloadLinks,
                google_drive_links: googleDriveLinks,
                language
              }"
            />
            <GbButton
              :autofocus="true"
              label="Close Preview"
              @click="closeCallback"
              text
              class="text-md text-white"
              size="small"
            >
              <span class="material-icons-round">close</span> Close Preview
            </GbButton>
          </div>
        </div>
        <a
          class="fixed left-5 top-1/2 z-20 cursor-pointer rounded-full bg-gray-1000 px-2 py-1 font-bold text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline focus:outline-offset-2 focus:outline-gray-1000"
          @click="advancePage(-1)"
          @keyup.enter="advancePage(-1)"
          tabindex="0"
        >
          <span class="material-icons-round mt-1 text-2xl">chevron_left</span>
        </a>
        <a
          class="fixed right-5 top-1/2 z-20 cursor-pointer rounded-full bg-gray-1000 px-2 py-1 font-bold text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline focus:outline-offset-2 focus:outline-gray-1000"
          @click="advancePage(1)"
          @keyup.enter="advancePage(1)"
          tabindex="0"
        >
          <span class="material-icons-round mt-1 text-2xl">chevron_right</span>
        </a>
        <div class="absolute inset-0 z-10 inline-flex h-full flex-col gap-5 pt-20">
          <div
            v-for="(img, index) in screenshots"
            :key="keyForScreenshotIndex(index)"
            :id="keyForScreenshotIndex(index)"
            ref="imageRefs"
            class="static mx-auto scroll-mt-20 last:pb-96"
          >
            <IKImageS3Wrapper
              class="resource_thumbnail_image"
              :aws-url="img"
              :alt="`Preview image ${index + 1}`"
              :query-parameters="previewImageTransformations"
            />
          </div>
        </div>
        <div class="fixed bottom-12 z-20 w-full">
          <div
            class="gap mx-auto flex w-60 flex-col items-center justify-center rounded-full bg-gray-1000 p-4 text-white opacity-80"
          >
            <span>Full resource contains</span>
            <span class="font-bold">{{ numPages }} pages</span>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
